import React, { useCallback, useEffect, useState } from 'react';
import { Briefcase, Clock, Users } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { aggregatorsApi, AggregatorPayout, AggregatorSettings } from '../../api/aggregators';
import { isApprovalRequired } from '../../api/approvals';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { StatCard } from '../../components/ui/StatCard';
import { AggregatorsPanel } from './AggregatorsPanel';
import { ApplicationsPanel } from './ApplicationsPanel';
import { SettingsPanel } from './SettingsPanel';
import { date, naira, sectionNote, sectionTitle, td, th } from './shared';

type Tab = 'applications' | 'aggregators' | 'rules';

/**
 * Aggregators: people who bring businesses to PayIQ Business and earn a
 * share of what PayIQ makes from each one.
 *
 * Applications come in from the consumer app's Invite friends screen. Once
 * approved, every fee PayIQ earns from a business that signed up with their
 * code earns them their share, and they withdraw it into their own PayIQ
 * balance. This page is where they are approved, watched and, when they
 * break the rules, stopped.
 */
export const Aggregators: React.FC = () => {
  usePage('Aggregators');
  const toast = useToast();
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('applications');
  const [settings, setSettings] = useState<AggregatorSettings | null>(null);
  const [stuck, setStuck] = useState<AggregatorPayout[]>([]);
  const [waiting, setWaiting] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [resolving, setResolving] = useState<{ payout: AggregatorPayout; outcome: 'completed' | 'failed' } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);

  const loadTop = useCallback(async () => {
    try {
      const [s, st, pending] = await Promise.all([
        aggregatorsApi.settings(),
        aggregatorsApi.stuckPayouts(),
        aggregatorsApi.applications({ status: 'pending', limit: 1 }),
      ]);
      setSettings(s);
      setStuck(st);
      setWaiting(pending.meta.total);
    } catch (err) {
      toast.error(err, 'Failed to load aggregators.');
    }
  }, [toast]);

  useEffect(() => {
    void loadTop();
  }, [loadTop]);

  async function resolve() {
    if (!resolving) return;
    setBusy(true);
    try {
      const result = await aggregatorsApi.resolvePayout(resolving.payout.id, resolving.outcome);
      if (isApprovalRequired(result)) {
        toast.success('Sent for a second admin to approve.', { to: '/approvals', label: 'View Approvals' });
      } else {
        toast.success(resolving.outcome === 'completed' ? 'Recorded as paid.' : 'Recorded as failed. The money is theirs to withdraw again.');
      }
      setResolving(null);
      await loadTop();
    } catch (err) {
      toast.error(err, 'Could not resolve that withdrawal.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card padding={20}>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--neutral-600)', maxWidth: 720 }}>
          Aggregators bring businesses to PayIQ Business with their invitation code, and earn a share of what PayIQ
          makes from each one. They see each business&apos;s name and monthly volume, never its transactions.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <StatCard label="Applications waiting" value={waiting ?? '—'} icon={Users} accent={waiting ? 'purple' : 'neutral'} />
          <StatCard
            label="Default share"
            value={settings ? `${settings.defaultCommissionPercent}%` : '—'}
            icon={Briefcase}
            accent="blue"
          />
          <StatCard
            label="Withdrawals stuck over 30 min"
            value={stuck.length}
            icon={Clock}
            accent={stuck.length ? 'purple' : 'neutral'}
          />
        </div>
      </Card>

      {stuck.length > 0 && (
        <Card padding={20}>
          <h3 style={sectionTitle}>Withdrawals the bank never answered for</h3>
          <p style={sectionNote}>
            Sent over 30 minutes ago and still unconfirmed. They settle by themselves as soon as Safe Haven answers.
            Check the payouts account&apos;s statement, then say what happened. A second admin approves.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--neutral-200)' }}>
                {['Sent', 'Paid', 'Reference', ''].map((h) => (
                  <th key={h} style={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stuck.map((p) => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                  <td style={td}>{date(p.createdAt)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{naira(p.net)}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{p.reference}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {can('aggregators.payouts') ? (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button variant="secondary" onClick={() => setResolving({ payout: p, outcome: 'completed' })}>
                          Went through
                        </Button>
                        <Button variant="ghost" onClick={() => setResolving({ payout: p, outcome: 'failed' })}>
                          Didn&apos;t
                        </Button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <div style={{ display: 'flex', gap: 6 }}>
        {(
          [
            ['applications', 'Applications'],
            ['aggregators', 'Aggregators'],
            ['rules', 'Rules'],
          ] as Array<[Tab, string]>
        ).map(([key, text]) => (
          <Button key={key} variant={tab === key ? 'primary' : 'ghost'} onClick={() => setTab(key)}>
            {text}
          </Button>
        ))}
      </div>

      {tab === 'applications' ? (
        <ApplicationsPanel
          defaultRate={settings?.defaultCommissionPercent ?? null}
          onChanged={() => {
            setRefreshKey((k) => k + 1);
            void loadTop();
          }}
        />
      ) : null}
      {tab === 'aggregators' ? <AggregatorsPanel refreshKey={refreshKey} /> : null}
      {tab === 'rules' && settings ? <SettingsPanel settings={settings} onSaved={setSettings} /> : null}

      <ConfirmDialog
        isOpen={!!resolving}
        title={resolving?.outcome === 'completed' ? 'Mark as paid' : 'Mark as failed'}
        message={
          resolving?.outcome === 'completed'
            ? 'Only if the payouts account statement shows the money left. The aggregator is told it is in their balance.'
            : 'Only if the statement shows nothing left. The amount becomes theirs to withdraw again.'
        }
        confirmLabel="Send for approval"
        loading={busy}
        onConfirm={() => void resolve()}
        onCancel={() => setResolving(null)}
      />
    </div>
  );
};
